import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getUserFriendlyError } from "@/lib/error-handler";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Save } from "lucide-react";
import { useState, useEffect } from "react";

const MODULES = [
  { key: "admins_roles", label: "Admins y Roles" },
  { key: "usuarios_comunidad", label: "Usuarios Comunidad" },
  { key: "reviews", label: "Reviews" },
  { key: "salas", label: "Salas" },
  { key: "ciudades", label: "Ciudades" },
  { key: "seo", label: "SEO" },
] as const;

const STANDARD_PERMS = [
  { key: "can_view", label: "Ver" },
  { key: "can_create", label: "Crear" },
  { key: "can_edit", label: "Editar" },
  { key: "can_delete", label: "Eliminar" },
] as const;

const SPECIAL_PERMS: Record<string, { key: string; label: string }[]> = {
  admins_roles: [
    { key: "can_manage_roles", label: "Gestionar roles" },
    { key: "can_manage_admins", label: "Gestionar admins" },
  ],
  usuarios_comunidad: [
    { key: "can_block_user", label: "Bloquear usuario" },
    { key: "can_edit_user_profile", label: "Editar perfil" },
  ],
  reviews: [
    { key: "can_moderate", label: "Moderar" },
  ],
  salas: [],
  ciudades: [],
  seo: [],
};

type PermRow = {
  id: string;
  module_key: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_moderate: boolean;
  can_manage_roles: boolean;
  can_manage_admins: boolean;
  can_block_user: boolean;
  can_edit_user_profile: boolean;
};

interface Props {
  roleId: string;
  roleLabel: string;
  roleName: string;
}

export function PermissionsMatrix({ roleId, roleLabel, roleName }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [localPerms, setLocalPerms] = useState<Record<string, Record<string, boolean>>>({});
  const [dirty, setDirty] = useState(false);

  const { data: permissions, isLoading } = useQuery({
    queryKey: ["admin-role-permissions", roleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_role_permissions")
        .select("*")
        .eq("role_id", roleId);
      if (error) throw error;
      return data as PermRow[];
    },
  });

  // Initialize local state from fetched data
  useEffect(() => {
    if (!permissions) return;
    const perms: Record<string, Record<string, boolean>> = {};
    for (const p of permissions) {
      perms[p.module_key] = {
        can_view: p.can_view,
        can_create: p.can_create,
        can_edit: p.can_edit,
        can_delete: p.can_delete,
        can_moderate: p.can_moderate,
        can_manage_roles: p.can_manage_roles,
        can_manage_admins: p.can_manage_admins,
        can_block_user: p.can_block_user,
        can_edit_user_profile: p.can_edit_user_profile,
      };
    }
    setLocalPerms(perms);
    setDirty(false);
  }, [permissions]);

  const toggle = (moduleKey: string, permKey: string) => {
    setLocalPerms((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [permKey]: !prev[moduleKey]?.[permKey],
      },
    }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const [moduleKey, perms] of Object.entries(localPerms)) {
        const row = permissions?.find((p) => p.module_key === moduleKey);
        if (!row) continue;
        const { error } = await supabase
          .from("admin_role_permissions")
          .update({
            can_view: perms.can_view,
            can_create: perms.can_create,
            can_edit: perms.can_edit,
            can_delete: perms.can_delete,
            can_moderate: perms.can_moderate,
            can_manage_roles: perms.can_manage_roles,
            can_manage_admins: perms.can_manage_admins,
            can_block_user: perms.can_block_user,
            can_edit_user_profile: perms.can_edit_user_profile,
          } as any)
          .eq("id", row.id);
        if (error) throw error;
      }

      // Audit log
      await supabase.from("admin_audit_log").insert({
        admin_id: (await supabase.auth.getUser()).data.user!.id,
        action: "edit_role_permissions",
        entity_type: "role",
        entity_id: roleId,
        details: localPerms,
      } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-role-permissions", roleId] });
      toast({ title: `Permisos de "${roleLabel}" actualizados` });
      setDirty(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: getUserFriendlyError(err), variant: "destructive" });
    },
  });

  if (isLoading) return <Skeleton className="h-32" />;

  const isSuperAdmin = roleName === "super_admin";

  return (
    <div className="rounded-lg border">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-display text-sm font-semibold">{roleLabel}</h3>
        {!isSuperAdmin && (
          <Button
            size="sm"
            disabled={!dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            <Save className="mr-2 h-3.5 w-3.5" />
            {saveMutation.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        )}
        {isSuperAdmin && (
          <span className="text-xs text-muted-foreground">Acceso total (no editable)</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-2 text-left font-medium">Módulo</th>
              {STANDARD_PERMS.map((p) => (
                <th key={p.key} className="px-3 py-2 text-center font-medium">{p.label}</th>
              ))}
              <th className="px-3 py-2 text-center font-medium">Acciones especiales</th>
            </tr>
          </thead>
          <tbody>
            {MODULES.map((mod) => {
              const modPerms = localPerms[mod.key] ?? {};
              const specials = SPECIAL_PERMS[mod.key] ?? [];

              return (
                <tr key={mod.key} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{mod.label}</td>
                  {STANDARD_PERMS.map((p) => (
                    <td key={p.key} className="px-3 py-3 text-center">
                      <Checkbox
                        checked={!!modPerms[p.key]}
                        disabled={isSuperAdmin}
                        onCheckedChange={() => toggle(mod.key, p.key)}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-3">
                    {specials.length > 0 ? (
                      <div className="flex flex-wrap gap-3">
                        {specials.map((sp) => (
                          <label key={sp.key} className="flex items-center gap-1.5 text-xs">
                            <Checkbox
                              checked={!!modPerms[sp.key]}
                              disabled={isSuperAdmin}
                              onCheckedChange={() => toggle(mod.key, sp.key)}
                            />
                            {sp.label}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
